import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Modal,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Add, AutoAwesome, Delete, ExpandMore } from '@mui/icons-material'
import ProfileStyle from './Style'
import Spinner from 'components/Spinner/Spinner'
import {
  useBulkGenerateSummaryMutation,
  useGetBulkGenerateSummaryVibeQuery,
  useGetSubscriptionQuery,
  useGetUserQuery,
  useGetVibesQuery,
  useGetAllVibesQuery,
  useCreateVibesMutation,
  useGetWeeklyContextHistoryQuery,
  useGetActiveWeeklyContextQuery,
} from 'graphql/generated/graphql'
import WeeklyContextModal from 'components/WeeklyContextModal/WeeklyContextModal'
import VibeTrainingModal from 'components/VibeTrainingModal/VibeTrainingModal'
import { usePageNavigation } from 'hooks/useNavigation'
import { format } from 'date-fns'
import IconButton from 'components/IconButton/IconButton'
import { errorMessage, successMessage } from 'components/toast/toast'

const inputStyle = {
  maxWidth: '360px',
  width: '100%',
  fontStyle: 'italic',
  opacity: 0.6,
  border: '1px solid #ffffff',
  borderRadius: 'inherit',
  color: '#ffffff',
  '& .MuiInputBase-input': { color: '#ffffff' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ffffff' },
    '&:hover fieldset': { borderColor: '#ffffff' },
    '&.Mui-focused fieldset': { borderColor: '#ffffff' },
  },
  '&.Mui-disabled': {
    opacity: 0.4,
    backgroundColor: '#333333',
    borderColor: '#cccccc',
    '& .MuiInputBase-input': { color: '#aaaaaa' },
  },
}

const inputVibesStyle = {
  width: '50%',
  fontStyle: 'italic',
  opacity: 0.6,
  borderRadius: 'inherit',
  color: '#ffffff',
  '& .MuiInputBase-input': { color: '#ffffff' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ffffff' },
    '&:hover fieldset': { borderColor: '#ffffff' },
    '&.Mui-focused fieldset': { borderColor: '#ffffff' },
  },
  '& .MuiInputLabel-root': {
    color: '#ffffff',
    '&.Mui-focused': { color: '#ffffff' },
  },
  '&.Mui-disabled': {
    opacity: 0.4,
    backgroundColor: '#333333',
    borderColor: '#cccccc',
    '& .MuiInputBase-input': { color: '#aaaaaa' },
  },
}

interface VibesFormProps {
  campaignId: string
}

const VibesForm: React.FC<VibesFormProps & { active?: boolean }> = ({
  campaignId,
  active,
}) => {
  const [vibes, setVibes] = useState<string[]>([])
  const [error, setError] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [trainingModalOpen, setTrainingModalOpen] = useState(false)

  const {
    loading: getVibeLoading,
    error: vibeError,
    data: vibeData,
    refetch: refetchVibes,
  } = useGetAllVibesQuery({
    skip: !active,
  })


  const [createVibes, { loading: vibeLoading }] = useCreateVibesMutation()

  useEffect(() => {
    if (active) {
      void refetchVibes()
    }
  }, [active, refetchVibes])

  useEffect(() => {
    if (vibeData?.getAllVibes) {
      setVibes([...vibeData.getAllVibes])
      setFieldErrors(Array(vibeData.getAllVibes.length).fill(''))
    }
  }, [vibeData])


  const handleChange = (index: number, value: string) => {
    const updated = [...vibes]
    updated[index] = value
    setVibes(updated)
    const updatedErrors = [...fieldErrors]
    updatedErrors[index] = ''
    setFieldErrors(updatedErrors)
    if (error) setError('')
  }

  const handleAdd = () => {
    const newFieldErrors = vibes.map((v) =>
      v.trim() === '' ? 'Please fill this field first' : ''
    )
    setFieldErrors(newFieldErrors)
    if (newFieldErrors.every((err) => err === '')) {
      setVibes([...vibes, ''])
      setFieldErrors([...newFieldErrors, ''])
    }
  }

  const handleRemove = (index: number) => {
    if (vibes.length > 5) setVibes(vibes.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    const newFieldErrors = vibes.map((v) =>
      v.trim() === '' ? 'Please fill this field first' : ''
    )
    setFieldErrors(newFieldErrors)

    // If any field is empty, prevent save and show toast
    if (newFieldErrors.some((err) => err !== '')) {
      return
    }

    const filledVibes = vibes.filter((v) => v.trim() !== '')

    if (filledVibes.length < 5) {
      setError('Please enter at least 5 vibes before saving.')
      return
    }

    setError('')

    try {
      await createVibes({
        variables: {
          data: JSON.stringify(filledVibes),
        },
      })

      successMessage('Vibes saved successfully!')
    } catch (err) {
      errorMessage('Failed to save vibes. Please try again.')
    }
  }

  if (getVibeLoading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderTopColor: '#ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography sx={{ mt: 2, color: '#ffffff' }}>
          Loading your vibes...
        </Typography>
      </Box>
    )
  }

  // Show error if failed to load
  if (vibeError) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert
          severity="error"
          sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#ff5252' }}
        >
          Failed to load vibes. Please refresh the page.
        </Alert>
      </Box>
    )
  }

  //   const { data, loading, errors, refetch } = useGetAllVibesQuery({
  //   skip: !active,
  // });

  // useEffect(() => {
  //   if (active) {
  //     void refetchs();
  //   }
  // }, [active, refetch]);

  // if (loading) return <p>Loading vibes...</p>;
  // if (errors) return <p>Error loading vibes</p>;

  // const vibesss = data?.getAllVibes || [];
  // console.log(vibesss , 'vibesss')

  const handleTrainingSuccess = () => {
    void refetchVibes()
  }

  return (
    <Box sx={{ p: 2, maxWidth: '100vw' }}>
      <Typography variant="h4" className="font-medium mb-3 text-[#fff]">
        Your Comment Vibes
      </Typography>

      <ProfileStyle.SubTitle
        sx={{ fontSize: '16px', fontStyle: 'italic', mb: 2 }}
      >
        Train the AI to write comments that sound like you.
      </ProfileStyle.SubTitle>

      {/* Train Your Style Card */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(236,72,153,0.15) 100%)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: 2,
          p: 3,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesome sx={{ color: '#a855f7' }} />
            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>
              Interactive Style Training
            </Typography>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            See real posts and write comments for them. This helps the AI learn your authentic voice
            much better than typing random examples.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setTrainingModalOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
            },
            py: 1.5,
            px: 3,
            whiteSpace: 'nowrap',
          }}
        >
          Train Your Style
        </Button>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />

      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, mb: 2 }}>
        Or manually enter example comments below:
      </Typography>

      {vibes.map((vibe, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <ProfileStyle.TextFieldContainer style={{ flex: 1 }}>
              <span className="mr-[20px]">{`Vibe ${index + 1}`}</span>
              <TextField
                sx={inputVibesStyle}
                variant="outlined"
                value={vibe}
                minRows={2}
                fullWidth
                onChange={(e) => handleChange(index, e.target.value)}
              />
              <Button
                color="error"
                onClick={() => handleRemove(index)}
                sx={{ ml: 2, minWidth: '36px', padding: '6px' }}
              >
                <Delete fontSize="medium" />
              </Button>
            </ProfileStyle.TextFieldContainer>
          </Box>

          {fieldErrors[index] && (
            <Typography sx={{ mt: 0.5, color: 'red', fontSize: '0.8rem' }}>
              {fieldErrors[index]}
            </Typography>
          )}
        </Box>
      ))}

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAdd}
          sx={{ py: 1.5, fontSize: '16px', width: '150px' }}
        >
          + Add More
        </Button>

        <Button
          variant="contained"
          color="info"
          onClick={() => {
            void handleSave()
          }}
          sx={{ py: 1.5, fontSize: '16px', width: '150px' }}
          disabled={vibeLoading}
        >
          {vibeLoading ? 'Saving...' : 'Save Vibes'}
        </Button>
      </Box>

      {error && <Typography sx={{ mt: 2, color: 'red' }}>{error}</Typography>}

      <VibeTrainingModal
        open={trainingModalOpen}
        onClose={() => setTrainingModalOpen(false)}
        onSuccess={handleTrainingSuccess}
      />
    </Box>
  )
}
// ---------------------- Weekly Context Tab ----------------------
interface WeeklyContextData {
  books?: string[]
  activities?: string[]
  media?: string[]
  topics?: string[]
  freeform?: string
}

const WeeklyContextTab: React.FC<{ active?: boolean }> = ({ active }) => {
  const [modalOpen, setModalOpen] = useState(false)

  const {
    data: activeContext,
    loading: activeLoading,
    refetch: refetchActive,
  } = useGetActiveWeeklyContextQuery({
    skip: !active,
  })

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useGetWeeklyContextHistoryQuery({
    variables: { limit: 10 },
    skip: !active,
  })

  useEffect(() => {
    if (active) {
      void refetchActive()
      void refetchHistory()
    }
  }, [active, refetchActive, refetchHistory])

  const handleModalSuccess = () => {
    void refetchActive()
    void refetchHistory()
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return format(date, 'MMM d, yyyy')
  }

  const renderContextItems = (ctx: WeeklyContextData) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {ctx.books && ctx.books.length > 0 && (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5 }}>
            Books/Articles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ctx.books.map((item, i) => (
              <Chip key={i} label={item} size="small" sx={{ bgcolor: 'rgba(168,85,247,0.2)', color: '#fff' }} />
            ))}
          </Box>
        </Box>
      )}
      {ctx.activities && ctx.activities.length > 0 && (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5 }}>
            Activities
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ctx.activities.map((item, i) => (
              <Chip key={i} label={item} size="small" sx={{ bgcolor: 'rgba(236,72,153,0.2)', color: '#fff' }} />
            ))}
          </Box>
        </Box>
      )}
      {ctx.media && ctx.media.length > 0 && (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5 }}>
            Shows/Movies
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ctx.media.map((item, i) => (
              <Chip key={i} label={item} size="small" sx={{ bgcolor: 'rgba(59,130,246,0.2)', color: '#fff' }} />
            ))}
          </Box>
        </Box>
      )}
      {ctx.topics && ctx.topics.length > 0 && (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5 }}>
            Topics of Interest
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ctx.topics.map((item, i) => (
              <Chip key={i} label={item} size="small" sx={{ bgcolor: 'rgba(34,197,94,0.2)', color: '#fff' }} />
            ))}
          </Box>
        </Box>
      )}
      {ctx.freeform && (
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 0.5 }}>
            Current Vibe
          </Typography>
          <Typography sx={{ color: '#fff', fontStyle: 'italic' }}>{ctx.freeform}</Typography>
        </Box>
      )}
    </Box>
  )

  if (activeLoading || historyLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid rgba(255, 255, 255, 0.1)',
            borderTopColor: '#ffffff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography sx={{ mt: 2, color: '#ffffff' }}>Loading weekly context...</Typography>
      </Box>
    )
  }

  const currentContext = activeContext?.getActiveWeeklyContext?.context as WeeklyContextData | undefined
  const history = historyData?.getWeeklyContextHistory ?? []

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" className="font-medium text-[#fff]">
            Weekly Context
          </Typography>
          <ProfileStyle.SubTitle sx={{ fontSize: '16px', fontStyle: 'italic' }}>
            Share what you&apos;ve been up to recently to keep your AI comments fresh and personal.
          </ProfileStyle.SubTitle>
        </Box>
        <Button
          variant="contained"
          onClick={() => setModalOpen(true)}
          startIcon={<AutoAwesome />}
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)' },
          }}
        >
          Update This Week
        </Button>
      </Box>

      {currentContext && (Object.values(currentContext).some((v) => (Array.isArray(v) ? v.length > 0 : !!v))) ? (
        <Box
          sx={{
            bgcolor: 'rgba(168,85,247,0.1)',
            border: '1px solid rgba(168,85,247,0.3)',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ color: '#a855f7', fontWeight: 600, mb: 2 }}>
            Current Week&apos;s Context
          </Typography>
          {renderContextItems(currentContext)}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.05)',
            border: '1px dashed rgba(255,255,255,0.2)',
            borderRadius: 2,
            p: 4,
            mb: 3,
            textAlign: 'center',
          }}
        >
          <AutoAwesome sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 48, mb: 2 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>
            No context added for this week yet.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
            Click &quot;Update This Week&quot; to share what you&apos;ve been up to.
          </Typography>
        </Box>
      )}

      {history.length > 1 && (
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>Past Weeks</Typography>
          {history.slice(1).map((item) => {
            const ctx = item.context as WeeklyContextData
            return (
              <Accordion
                key={item.id}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  mb: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fff' }} />}>
                  <Typography>Week of {formatDate(item.weekStart)}</Typography>
                </AccordionSummary>
                <AccordionDetails>{renderContextItems(ctx)}</AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      )}

      <WeeklyContextModal open={modalOpen} onClose={() => setModalOpen(false)} onSuccess={handleModalSuccess} />
    </Box>
  )
}

// ---------------------- Profile Component ----------------------
export default function Profile(): JSX.Element {
  const { data, loading } = useGetUserQuery()
  const {
    data: subsData,
    loading: subsLoading,
    refetch,
  } = useGetSubscriptionQuery()
  const { navigateToSubscriptions } = usePageNavigation()
  const [tabIndex, setTabIndex] = useState(0)
  const [openModal, setOpenModal] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [successModal, setSuccessModal] = useState(false)

  // const handleCancelSubscription = async (): Promise<void> => {
  //   try {
  //     const response = await fetch('/cancel-subscription', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         subscriptionId: subsData?.getSubscription?.stripeSubscriptionId,
  //       }),
  //     })
  //     const result = await response.json()
  //     if (result.canceled)
  //       alert('You have successfully canceled your subscription')
  //   } catch (e) {
  //     console.log(e)
  //   }
  // }

  if (loading || subsLoading || cancelLoading) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Spinner />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <ProfileStyle.Title>My Profile</ProfileStyle.Title>
      <ProfileStyle.SubTitle>
        Manage your personal and subscription details here.
      </ProfileStyle.SubTitle>
      <Divider sx={{ borderColor: '#ffffff69', mb: 2 }} />

      {/* ---------------- Tabs ---------------- */}
      {/* <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        sx={{ mb: 2 }}
      >
        <Tab label="General" />
        <Tab label="Vibes" />
      </Tabs> */}
      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        sx={{
          mb: 2,
          '.MuiTab-root': {
            color: '#ffffff', // inactive tab color
          },
          '.Mui-selected': {
            color: '#1976d2', // active tab color
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#1976d2', // active underline
          },
        }}
      >
        <Tab label="General" />
        <Tab label="Vibes" />
        <Tab label="Weekly Context" />
      </Tabs>

      {/* ---------------- Tab Panels ---------------- */}
      {tabIndex === 0 && (
        <Box>
          <h3 style={{ marginTop: '20px' }}>Account Information</h3>
          <ProfileStyle.TextFieldContainer>
            <p>Username</p>
            <TextField
              sx={inputStyle}
              variant="outlined"
              disabled
              value={data?.user?.username}
            />
          </ProfileStyle.TextFieldContainer>
          <ProfileStyle.TextFieldContainer>
            <p>Email</p>
            <TextField
              sx={inputStyle}
              variant="outlined"
              disabled
              value={data?.user?.email}
            />
          </ProfileStyle.TextFieldContainer>

          <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>
            Subscription
          </h3>
          <ProfileStyle.SubTitle sx={{ fontSize: '14px', fontStyle: 'italic' }}>
            Select the plan that&apos;s best for you and update your payment
            information. You can cancel your subscription at any time.
          </ProfileStyle.SubTitle>
          <ProfileStyle.PlanBox>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ margin: 0 }}>
                {subsData?.getSubscription?.planName ?? 'Free Plan'}
              </h3>
              <span style={{ color: '#ccc', fontSize: '12px' }}>
                {subsData?.getSubscription?.planName ? '29$ p/month' : 'Free'}
              </span>
            </Box>
            <Button
              variant="outlined"
              color="info"
              onClick={navigateToSubscriptions}
            >
              Change plan
            </Button>
          </ProfileStyle.PlanBox>
        </Box>
      )}

      {tabIndex === 1 && <VibesForm campaignId="" active={tabIndex === 1} />}

      {tabIndex === 2 && <WeeklyContextTab active={tabIndex === 2} />}
    </Box>
  )
}
